import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { APP } from 'app/AppRoutes';
import { RootState } from 'redux/root.reducer';
import {
  CFCProps,
  APP_URL,
  SCREEN_SIZE,
  AssetOwnerTableRowData,
  Params
} from 'types';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import {
  Button,
  ContentSwitcher,
  Switch,
  Loading
} from 'carbon-components-react';
import { Renew16 } from '@carbon/icons-react';
import {
  MainContainer,
  MainHeaderContainer,
  CustomRightHeaderPanel
} from 'layouts/components';
import {
  listReferralsEmailsStart,
  listResponsesEmailsStart,
  getReferralEmailDetailStart,
  getResponsesEmailDetailStart,
  previewEmailStart,
  downloadEmailBodyStart,
  clearAllEmails,
  previewEmailSuccess,
  resendEmailStart,
  resendToOtherEmailStart
} from 'redux/emails';
import {
  ListView,
  DetailPanel,
  SearchFilter,
  PreviewModal
} from './components';
import { SendToOtherModal } from 'components';
import {
  toLocalDateTimeFormatWithSeconds,
  getEmailAttachment,
  getNewDate
} from 'utilities/helper.util';
import { convertEmaliStatusTolabel } from 'appConstants/emails';
import { useComponentMount } from 'utilities/hooks.util';

const PAGE_SIZE = 10;

const filterStringReducer = (accumulator: string[], currentValue: any) => {
  const [key, value] = currentValue;

  switch (key) {
    case 'status':
      if (value.length > 0) accumulator.push(`${key}:${value.join('|')}`);
      return accumulator;
    case 'subject':
      if (value) accumulator.push(`subject:%${value}%`);
      return accumulator;
    case 'dateRange':
      return accumulator;
    default:
      if (value) accumulator.push(`${key}:${value}`);
      return accumulator;
  }
};

const AssetOnwerEmails: React.FC<Props> = ({
  isListing,
  resending,
  previewing,
  downloading,
  user,
  rawEmails,
  screenSize,
  searchFillter,
  previewEmail,
  emailBodyForDownload
}) => {
  const overallContainerRef = useRef(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const [isApplyFilter, setIsApplyFilter] = useState<boolean>(false);
  const [isRefferal, setIsRefferal] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [pageOffset, setPageOffset] = useState<number>(0);
  const [filterString, setFilterString] = useState<string>('');
  const [emails, setEmails] = useState<AssetOwnerTableRowData[]>([]);
  const [isPannelExpanded, setIsPannelExpanded] = useState<boolean>(false);
  const [currentJobId, setCurrentJobId] = useState<number | undefined>();
  const [currentReferralId, setCurrentReferralId] = useState<
    number | undefined
  >();
  const [showSendToOtherModal, setShowSendToOtherModal] = useState<boolean>(
    false
  );
  const [sendToOtherId, setSendToOtherId] = useState<string>();
  const isEventOpen = useRef<boolean>(false);
  const isAttachmentOpen = useRef<boolean>(true);
  const [disableEmailResend, setDisableEmailResend] = useState<boolean>(false);

  const onClickCloseButton = () => {
    setIsPannelExpanded(false);
    isEventOpen.current = false;
    isAttachmentOpen.current = true;
  };

  const onViewDetail = useCallback(
    (id: string, disableEmailResend: boolean) => {
      const email = rawEmails?.OutboundEmails?.find((e) => e.id === Number(id));
      if (email) {
        setIsPannelExpanded(true);
        setCurrentReferralId(email.Referral.externalId);
        setCurrentJobId(email.Referral.Enquiry.externalId);
        isRefferal
          ? dispatch(getReferralEmailDetailStart({ emailId: email.id }))
          : dispatch(
              getResponsesEmailDetailStart({
                emailId: email.id,
                enquiryId: email.Referral.Enquiry.id,
                referralId: email.Referral.id,
                responseId: email.Response.id
              })
            );
        setDisableEmailResend(disableEmailResend);
      }
    },
    [dispatch, isRefferal, rawEmails]
  );

  const onResend = (id: string) => {
    dispatch(resendEmailStart({ emailId: id }));
  };

  const onSendToOther = (id: string) => {
    setShowSendToOtherModal(true);
    setSendToOtherId(id);
  };

  const onClickSend = (val: any) => {
    const emailAddress = val;
    const emailId = sendToOtherId;
    dispatch(resendToOtherEmailStart({ emailId, emailAddress }));
    setShowSendToOtherModal(false);
  };

  const onPreviewEmail = (id: string) => {
    dispatch(previewEmailStart({ emailId: id }));
  };

  const onDownload = (id: string) => {
    dispatch(downloadEmailBodyStart({ emailId: id, direction: 'inbound' }));
  };

  const handlePageChange = ({
    page,
    pageSize
  }: {
    page: number;
    pageSize: number;
  }) => {
    setPageOffset((page - 1) * pageSize);
    setCurrentPage(page);
    setPageSize(pageSize);
    setIsApplyFilter(true);
  };

  const handleClosePreview = () => {
    dispatch(previewEmailSuccess(undefined));
  };
  const handleApply = () => {
    setIsApplyFilter(true);
  };
  const isEmailOver90Days = useCallback((createdDate: string) => {
    const daysDifferenceThreshold = 90;
    const todayDate = getNewDate().startOf('day').utc();
    const emailCreatedDate = getNewDate(createdDate).startOf('day').utc();
    const daysDifference = todayDate.diff(emailCreatedDate, 'day');
    if (daysDifference > daysDifferenceThreshold) {
      return true;
    } else {
      return false;
    }
  }, []);

  const fetchData = useCallback(() => {
    const queryParams: Params = {
      fields: '*',
      limit: pageSize,
      offset: pageOffset,
      filter: filterString
    };

    if (isRefferal) {
      filterString && dispatch(listReferralsEmailsStart(queryParams));
    } else {
      filterString && dispatch(listResponsesEmailsStart(queryParams));
    }
    setIsApplyFilter(false);
  }, [dispatch, isRefferal, pageSize, pageOffset, filterString]);

  useEffect(() => {
    setIsRefferal(routeMatch.url === APP_URL.REFERRALS_EMAILS);
    if (isRefferal && routeMatch.url !== APP_URL.REFERRALS_EMAILS) {
      setPageOffset(0);
      setCurrentPage(1);
      dispatch(clearAllEmails());
    } else if (!isRefferal && routeMatch.url !== APP_URL.RESPONSES_EMAILS) {
      setPageOffset(0);
      setCurrentPage(1);
      dispatch(clearAllEmails());
    }
  }, [dispatch, routeMatch, isRefferal]);

  useEffect(() => {
    if (!isApplyFilter) return;
    user && fetchData();
  }, [fetchData, isApplyFilter, user]);

  useEffect(() => {
    const filterArr = Object.entries({
      ...searchFillter
    });
    setPageOffset(0);
    setEmails([]);
    searchFillter &&
      setFilterString(filterArr.reduce(filterStringReducer, []).join(','));
  }, [searchFillter]);

  useEffect(() => {
    if (rawEmails) {
      const emails = rawEmails?.OutboundEmails?.map((email: any) => {
        const createdAt = isRefferal
          ? email.createdAt
          : email.createdAtResponse;
        const refResCreatedAt = isRefferal
          ? email.createdAtReferral
          : email.createdAtResponse;
        return {
          id: String(email?.id),
          sent: createdAt ? toLocalDateTimeFormatWithSeconds(createdAt) : '',
          referral: email?.Referral?.externalId,
          job: email?.Referral?.Enquiry?.externalId,
          to: email?.to,
          status: convertEmaliStatusTolabel(email.status),
          inboundEmailId: email?.Response?.inboundEmailId,
          disableEmailResend: isEmailOver90Days(refResCreatedAt)
        };
      });
      setEmails(emails);
      setTotalItems(rawEmails.Info?.count || 0);
    }
  }, [rawEmails, isEmailOver90Days, isRefferal]);

  useEffect(() => {
    if (emailBodyForDownload) {
      const attachment: any = emailBodyForDownload.Attachments.find(
        (file: any) => file.mimeType === 'message/rfc822'
      );
      getEmailAttachment({
        url: attachment.url,
        attachmentName: attachment.name
      });
    }
  }, [emailBodyForDownload]);

  useComponentMount(() => {
    return () => {
      dispatch(clearAllEmails());
    };
  });

  return (
    <div
      className="ao-emails-history container--wrapper"
      id="ao-emails-history"
      data-testid="ao-emails-history"
      ref={overallContainerRef}
    >
      <MainHeaderContainer enableHeaderGlobalBar={true}>
        <CustomRightHeaderPanel
          panelHeadingLabel="REF"
          panelHeading={currentReferralId}
          isExpanded={isPannelExpanded}
          closeButtonHandler={onClickCloseButton}
        >
          <DetailPanel
            isRefferal={isRefferal}
            isEventOpen={isEventOpen}
            isAttachmentOpen={isAttachmentOpen}
            currentJobId={currentJobId}
            onClose={onClickCloseButton}
            handleSendToOther={onSendToOther}
            disableEmailResend={disableEmailResend}
          />
        </CustomRightHeaderPanel>
      </MainHeaderContainer>
      <MainContainer>
        <section className="ao-emails-history-header">
          <div className="page--heading">
            <h4 className="bx--data-table-header__title">Emails</h4>
            {screenSize !== SCREEN_SIZE.SM && screenSize !== SCREEN_SIZE.MD && (
              <p className="bx--data-table-header__description">
                Review and manage your enquiry{' '}
                {isRefferal ? 'referrals' : 'responses'}
              </p>
            )}
          </div>
          <div className="ao-emails-history-controls">
            <Button
              hasIconOnly
              renderIcon={Renew16}
              tooltipAlignment="center"
              tooltipPosition="bottom"
              iconDescription="Refresh"
              kind="ghost"
              size="field"
              onClick={() => fetchData()}
            />
            <SearchFilter
              isRefferal={isRefferal}
              mainContainerRef={overallContainerRef}
              onApply={handleApply}
            />
            <div className="ao-emails-history-switcher">
              <ContentSwitcher selectedIndex={isRefferal ? 0 : 1}>
                <Switch
                  name={'referrals-emails'}
                  text="Referrals"
                  onClick={() => history.push(APP_URL.REFERRALS_EMAILS)}
                />
                <Switch
                  name={'responses-emails'}
                  text="Responses"
                  onClick={() => history.push(APP_URL.RESPONSES_EMAILS)}
                />
              </ContentSwitcher>
            </div>
          </div>
        </section>
        <section className="ao-emails-history-contents">
          <ListView
            isRefferal={isRefferal}
            emails={emails}
            handlePageChange={handlePageChange}
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={totalItems}
            handleViewDetail={onViewDetail}
            handleResend={onResend}
            handleDownload={onDownload}
            handlePreviewEmail={onPreviewEmail}
            handleSendToOther={onSendToOther}
          />
        </section>
      </MainContainer>
      {(isListing ||
        (resending && !isPannelExpanded) ||
        previewing ||
        downloading) && <Loading withOverlay={true} />}
      {previewEmail &&
        ReactDOM.createPortal(
          <PreviewModal onClose={() => handleClosePreview()} />,
          document.querySelector(`#${APP}`) || document.body
        )}

      <SendToOtherModal
        isModalOpen={showSendToOtherModal}
        onModalClose={() => setShowSendToOtherModal(false)}
        onClickSend={onClickSend}
      />
    </div>
  );
};

const mapStateToProps = ({ emailCache, session }: RootState) => ({
  isListing: emailCache.listing,
  resending: emailCache.resending,
  previewing: emailCache.previewing,
  downloading: emailCache.downloading,
  screenSize: session.screenSize,
  user: session.currentUser,
  rawEmails: emailCache.emails,
  searchFillter: emailCache.filters,
  previewEmail: emailCache.emailPreview,
  emailBodyForDownload: emailCache.emailBodyDownload
});

const connector = connect(mapStateToProps);

type Props = CFCProps<typeof connector>;

export default React.memo(connector(AssetOnwerEmails));
